import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { Form, Formik, FormikConfig } from "formik";
import { FC } from "react";

import { UrlMappingMatchType } from "~/src/__generated__/schema";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormControlGroup } from "~/src/components/common/forms/FormControlGroup";
import { FormikDateTimeField } from "~/src/components/common/forms/FormikDateTimeField";
import { FormikFormButtons } from "~/src/components/common/forms/FormikFormButtons";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { FormikSwitchField } from "~/src/components/common/forms/FormikSwitchField";
import { FormikTextareaField } from "~/src/components/common/forms/FormikTextareaField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { HelpIcon } from "~/src/components/common/HelpIcon";

import { MatchTypeHelpAlert } from "../MatchTypeHelpAlert";
import { UrlMappingConditionsEditor } from "../UrlMappingConditionsEditor";
import {
  ConditionRow,
  destinationModeHelp,
  destinationModeOptions,
  matchTypeOptions,
} from "../urlMappingFormUtils";

import { validationSchema } from "./validationSchema";

export type FormValues = {
  name: string;
  active: boolean;
  matchType: UrlMappingMatchType;
  priority: number;
  sourceUrl: string;
  sourceUrls: string;
  sourceHost: string;
  sourcePath: string;
  regexPattern: string;
  destinationUrl: string;
  destinationMode: "STATIC" | "TEMPLATE";
  conditionRows: ConditionRow[];
  startsAt: Date | null;
  endsAt: Date | null;
};

export type UrlMappingFormValues = FormValues;

export type Props = {
  initialValues: FormValues;
  onSubmit: FormikConfig<FormValues>["onSubmit"];
  onCancel: () => void;
};

export const UrlMappingForm: FC<Props> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount={false}
      onSubmit={onSubmit}
    >
      {({ values }) => (
        <Form>
          <VStack spacing="24px" alignItems="flex-start" width="100%">
            <FormControlGroup>
              <FormControl label="名称">
                <FormikTextField type="text" mode="fill" name="name" />
              </FormControl>
              <FormControl label="有効">
                <FormikSwitchField name="active" />
              </FormControl>
            </FormControlGroup>

            <FormControlGroup>
              <FormControl required label="マッチ種別">
                <FormikSelectField
                  name="matchType"
                  options={matchTypeOptions}
                />
              </FormControl>
              <FormControl required label="優先度">
                <FormikTextField type="number" mode="fill" name="priority" />
              </FormControl>
            </FormControlGroup>

            <MatchTypeHelpAlert matchType={values.matchType} />

            {values.matchType === "EXACT" && (
              <FormControl
                required
                label="ソースURL（1行1件）"
                help="複数の完全一致URLを同じ宛先にまとめる場合は、1行に1URLずつ入力してください。"
              >
                <FormikTextareaField
                  name="sourceUrls"
                  mode="fill"
                  placeholder={
                    "https://example.com/page-a\nhttps://example.com/page-b"
                  }
                />
              </FormControl>
            )}

            {values.matchType === "PREFIX" && (
              <FormControl required label="ソースURL">
                <FormikTextField type="text" mode="fill" name="sourceUrl" />
              </FormControl>
            )}

            {values.matchType === "REGEX" && (
              <FormControl required label="正規表現">
                <FormikTextField type="text" mode="fill" name="regexPattern" />
              </FormControl>
            )}

            {values.matchType === "HOST_PATH" && (
              <FormControlGroup>
                <FormControl required label="ソースホスト">
                  <FormikTextField type="text" mode="fill" name="sourceHost" />
                </FormControl>
                <FormControl required label="ソースパス">
                  <FormikTextField type="text" mode="fill" name="sourcePath" />
                </FormControl>
              </FormControlGroup>
            )}

            <FormControlGroup>
              <FormControl required label="宛先URL">
                <FormikTextField
                  type="text"
                  mode="fill"
                  name="destinationUrl"
                />
              </FormControl>
              <FormControl
                label="宛先モード"
                help={destinationModeHelp[values.destinationMode]}
              >
                <FormikSelectField
                  name="destinationMode"
                  options={destinationModeOptions}
                />
              </FormControl>
            </FormControlGroup>

            <Box width="100%">
              <HStack spacing="8px" mb="4px">
                <Text fontWeight="bold">追加条件</Text>
                <HelpIcon text="行はすべて AND。クエリ: 完全一致 / 正規表現。環境は Rails.env（production / staging / development など）。" />
              </HStack>
              {values.matchType === "TRANSFORM" && (
                <Text fontSize="sm" color="gray.600" mb="8px">
                  Transform では条件を空にするとすべての URL にマッチします。
                </Text>
              )}
              <UrlMappingConditionsEditor />
            </Box>

            <FormControlGroup>
              <FormControl
                label="開始日時"
                help="未設定の場合はいつでも有効。日付と時刻の両方を指定できます。"
              >
                <FormikDateTimeField
                  name="startsAt"
                  placeholder="開始日時を選択"
                />
              </FormControl>
              <FormControl
                label="終了日時"
                help="未設定の場合は期限なし。日付と時刻の両方を指定できます。"
              >
                <FormikDateTimeField
                  name="endsAt"
                  placeholder="終了日時を選択"
                />
              </FormControl>
            </FormControlGroup>

            <FormikFormButtons onCancelClick={onCancel} />
          </VStack>
        </Form>
      )}
    </Formik>
  );
};
